import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Loader2,
  Activity,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  ExternalLink,
  RotateCw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const WRITE_ROLES = ["owner", "admin", "researcher"];
const POLL_INTERVAL_MS = 3000;
const STAGE_ORDER = ["search", "crawl", "extract", "dedupe", "cluster", "findings"];

const STATUS_CFG = {
  queued: { Icon: Clock, cls: "border-slate-200 bg-slate-50 text-slate-700" },
  running: { Icon: Activity, cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  completed: { Icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  failed: { Icon: AlertCircle, cls: "border-rose-200 bg-rose-50 text-rose-700" },
  cancelled: { Icon: X, cls: "border-slate-200 bg-slate-50 text-slate-500" },
};

const SENTIMENT_BADGE = {
  positive: "border-emerald-200 bg-emerald-50 text-emerald-700",
  negative: "border-rose-200 bg-rose-50 text-rose-700",
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  mixed: "border-amber-200 bg-amber-50 text-amber-800",
};

function confidenceBand(score) {
  if (score >= 0.75) return "high";
  if (score >= 0.5) return "medium";
  return "low";
}

function ConfidenceBadge({ score }) {
  const { t } = useTranslation("findings");
  const band = confidenceBand(score);
  const cls =
    band === "high"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : band === "medium"
        ? "border-sky-200 bg-sky-50 text-sky-700"
        : "border-amber-200 bg-amber-50 text-amber-800";
  return (
    <Badge data-testid={`finding-confidence-${band}`} variant="outline" className={cls}>
      {t(`confidence.${band}`)} · {Math.round((score || 0) * 100)}%
    </Badge>
  );
}

function StatusBadge({ status }) {
  const { t } = useTranslation("runs");
  const cfg = STATUS_CFG[status] || STATUS_CFG.queued;
  const Icon = cfg.Icon;
  return (
    <Badge variant="outline" className={cfg.cls} data-testid={`run-status-${status}`}>
      <Icon className="mr-1 h-3 w-3" strokeWidth={2} />
      {t(`status.${status}`)}
    </Badge>
  );
}

function StageStrip({ run }) {
  const { t } = useTranslation("runs");
  const currentStage = run?.stage;
  const isActive = run?.status === "running" || run?.status === "queued";
  const completedStages = useMemo(() => {
    if (run?.status === "completed") return STAGE_ORDER;
    if (!currentStage) return [];
    const idx = STAGE_ORDER.indexOf(currentStage);
    return idx <= 0 ? [] : STAGE_ORDER.slice(0, idx);
  }, [run?.status, currentStage]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {STAGE_ORDER.map((s) => {
          const done = completedStages.includes(s);
          const active = isActive && currentStage === s;
          const cls = done
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : active
              ? "border-indigo-200 bg-indigo-50 text-indigo-700"
              : "border-slate-200 bg-slate-50 text-slate-500";
          return (
            <div
              key={s}
              data-testid={`stage-${s}${active ? "-active" : ""}`}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${cls}`}
            >
              {active ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
              ) : done ? (
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
              )}
              <span className="text-xs font-medium">{t(`stages.${s}`)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3 md:grid-cols-6">
        {STAGE_ORDER.map((s) => {
          const key = s === "search" ? "searched" : s === "crawl" ? "crawled" : s === "extract" ? "extracted" : s === "dedupe" ? "deduped" : s === "cluster" ? "clustered" : "findings";
          return (
            <div key={s} className="flex flex-col rounded-xl bg-slate-50 px-3 py-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                {t(`progress.${key}`)}
              </span>
              <span data-testid={`progress-${key}`} className="font-display text-sm font-semibold text-slate-900">
                {run?.progress?.[key] ?? 0}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FindingCard({ finding, evidence, sourcesById }) {
  const { t } = useTranslation(["findings", "common"]);
  const [open, setOpen] = useState(false);
  const supporting = (finding.supporting_evidence_ids || [])
    .map((eid) => evidence.find((e) => e.id === eid))
    .filter(Boolean);
  const contradictsCount = (finding.contradictions || []).length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`finding-${finding.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
            <Sparkles className="mr-1 h-3 w-3" strokeWidth={2} />
            {t(`type.${finding.type}`)}
          </Badge>
          <span className="text-xs text-slate-500">{finding.theme_label}</span>
          {contradictsCount > 0 && (
            <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
              <AlertTriangle className="mr-1 h-3 w-3" strokeWidth={2} />
              {t("card.contradictsOthers")}
            </Badge>
          )}
        </div>
        <ConfidenceBadge score={finding.confidence} />
      </div>
      {(finding.orphaned || (finding.persisted_evidence_count != null
                              && finding.persisted_evidence_count !== finding.effective_evidence_count)) && (
        <div
          data-testid={`finding-orphaned-notice-${finding.id}`}
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
        >
          {t("card.orphanedNotice")}
        </div>
      )}
      <p className="mt-4 font-display text-base leading-relaxed text-slate-900">
        {finding.text}
      </p>
      {finding.recommended_action && (
        <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            {t("card.recommendedAction")}
          </p>
          <p className="mt-1 text-sm text-amber-900">{finding.recommended_action}</p>
        </div>
      )}
      {finding.confidence_components && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid={`confidence-breakdown-${finding.id}`}>
          {Object.entries(finding.confidence_components).map(([k, v]) => (
            <div key={k} className="rounded-lg bg-slate-50 px-3 py-2" title={t(`confidence.tooltips.${k}`, { defaultValue: "" })}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">
                {t(`confidence.components.${k}`)}
              </p>
              <p className="mt-1 font-mono text-xs font-semibold text-slate-900">
                {Math.round((v || 0) * 100)}%
              </p>
              <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-indigo-400" style={{ width: `${Math.round((v || 0) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => { if (!finding.orphaned) setOpen((o) => !o); }}
        disabled={!!finding.orphaned}
        data-testid={`finding-toggle-evidence-${finding.id}`}
        title={finding.orphaned ? t("card.evidenceUnavailableTooltip") : undefined}
        className={
          finding.orphaned
            ? "mt-4 inline-flex cursor-not-allowed items-center gap-1 text-xs font-medium text-slate-400"
            : "mt-4 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
        }
      >
        {finding.orphaned ? (
          <AlertCircle className="h-3 w-3" strokeWidth={2} />
        ) : open ? (
          <ChevronUp className="h-3 w-3" strokeWidth={2} />
        ) : (
          <ChevronDown className="h-3 w-3" strokeWidth={2} />
        )}
        {finding.orphaned
          ? t("card.evidenceUnavailable")
          : (open ? t("card.hideEvidence") : t("card.showEvidence"))}
        {!finding.orphaned && (
          <>
            {" · "}{supporting.length}
            {supporting.length !== (finding.persisted_evidence_count || finding.effective_evidence_count || 0) && (
              <span className="ml-1 text-amber-600">
                ({t("card.missingEvidence", {
                  missing: (finding.persisted_evidence_count || 0) - supporting.length,
                  total: finding.persisted_evidence_count || 0,
                })})
              </span>
            )}
          </>
        )}
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {supporting.map((e) => (
            <li key={e.id} className="rounded-xl bg-slate-50 p-3 text-sm">
              <p className="text-slate-900">&quot;{e.quote}&quot;</p>
              <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                <a
                  href={e.source_url || sourcesById[e.source_id]?.url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-indigo-700 hover:underline"
                >
                  {(e.source_title || sourcesById[e.source_id]?.title || "source").slice(0, 60)}
                  <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                </a>
                <Badge variant="outline" className={SENTIMENT_BADGE[e.sentiment] || SENTIMENT_BADGE.neutral}>
                  {e.sentiment}
                </Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WarningsPanel({ run }) {
  const { t } = useTranslation("runs");
  const warnings = run?.warnings || [];
  if (!warnings.length) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="run-warnings-panel">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-700" strokeWidth={2} />
        <h3 className="font-display text-sm font-semibold text-amber-900">{t("warnings.title")}</h3>
      </div>
      <ul className="mt-2 space-y-1">
        {warnings.map((w, i) => (
          <li key={i} className="text-sm text-amber-900" data-testid={`run-warning-${w.code}`}>
            {t(`warnings.${w.code}`, { defaultValue: w.message_key || w.code, share: Math.round((w.share || 0) * 100), domain: w.dominant_domain || "" })}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchStrategyPanel({ run, expanded, onToggle }) {
  const { t } = useTranslation("runs");
  const queries = run?.queries || [];
  if (!queries.length) return null;
  const grouped = queries.reduce((acc, q) => {
    const k = q.source_type || "web";
    (acc[k] = acc[k] || []).push(q);
    return acc;
  }, {});
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="run-search-strategy">
      <button
        type="button"
        onClick={onToggle}
        data-testid="run-search-strategy-toggle"
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-900">{t("searchStrategy.title")}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{t("searchStrategy.subtitle")}</p>
        </div>
        <span className="text-xs font-medium text-indigo-700">
          {expanded ? t("searchStrategy.hideAll") : t("searchStrategy.showAll")} · {queries.length}
        </span>
      </button>
      {expanded && (
        <div className="mt-4 space-y-3">
          {Object.entries(grouped).map(([source_type, items]) => (
            <div key={source_type} data-testid={`run-strategy-group-${source_type}`}>
              {/* Phase 12 — per-source-type count badge so the user can see
                  the search-mix breakdown at a glance. */}
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {t(`searchStrategy.sourceTypes.${source_type}`, { defaultValue: source_type })}
                </p>
                <span
                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700"
                  data-testid={`run-strategy-group-count-${source_type}`}
                >
                  {items.length}
                </span>
              </div>
              <ul className="mt-1 space-y-1">
                {items.map((q, i) => (
                  <li key={i} className="rounded-lg bg-slate-50 px-3 py-1.5 font-mono text-xs text-slate-700">
                    {q.query}
                    {q.days ? (
                      <span className="ml-2 text-[10px] text-slate-400">
                        · {t("searchStrategy.daysSuffix", { days: q.days })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Phase 9 — Resume panel for failed/cancelled runs. PRIMARY action is
// "Resume from <Stage>"; demoted "Start a new run" stays as a secondary
// link back to the project. Shows a concise localized "stages remaining"
// hint so the user sees they're not paying twice.
function ResumePanel({ run, projectId, canWrite, onResumed }) {
  const { t } = useTranslation(["runs", "common", "errors"]);
  const navigate = useNavigate();
  const STAGE_ORDER_FULL = ["search", "relevance", "crawl", "extract", "dedupe", "cluster", "findings"];
  const completed = run.stages_completed || [];
  const remaining = STAGE_ORDER_FULL.filter((s) => !completed.includes(s));
  const nextStage = remaining[0] || null;
  const [resuming, setResuming] = useState(false);

  const onResume = async () => {
    setResuming(true);
    try {
      await api.post(`/research/runs/${run.id}/resume`);
      toast.success(t("runs:resume.toast"));
      onResumed?.();
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setResuming(false);
    }
  };

  const errorKey = run.error?.detail || "generic";
  const cancelled = run.status === "cancelled";
  const headlineKey = cancelled ? "runs:resume.cancelledTitle" : "runs:errors.title";
  const canShowResume = canWrite && nextStage && (run.status === "failed" || run.status === "cancelled");

  return (
    <div
      className={`rounded-2xl border p-6 ${cancelled ? "border-slate-200 bg-slate-50" : "border-rose-200 bg-rose-50"}`}
      data-testid={cancelled ? "run-cancelled-card" : "run-error-card"}
    >
      <div className="flex items-center gap-2">
        {cancelled ? (
          <X className="h-4 w-4 text-slate-700" strokeWidth={2} />
        ) : (
          <AlertCircle className="h-4 w-4 text-rose-700" strokeWidth={2} />
        )}
        <h3
          className={`font-display text-base font-semibold ${cancelled ? "text-slate-900" : "text-rose-900"}`}
        >
          {t(headlineKey)}
        </h3>
      </div>

      {!cancelled && run.error?.stage && (
        <p className="mt-2 text-xs text-rose-800/80">
          {t("runs:errors.stageLabel")}:{" "}
          <span className="font-mono">{t(`runs:stages.${run.error.stage}`)}</span>
        </p>
      )}

      {!cancelled && (
        <p className="mt-2 text-sm text-rose-900">
          {t(`runs:errors.${errorKey}`, { defaultValue: t("runs:errors.generic") })}
        </p>
      )}

      {canShowResume && (
        <div
          className={`mt-4 rounded-xl border px-4 py-3 ${cancelled ? "border-slate-300 bg-white" : "border-rose-300 bg-white"}`}
          data-testid="run-resume-hint"
        >
          <p
            className={`text-[11px] font-semibold uppercase tracking-wider ${cancelled ? "text-slate-600" : "text-rose-700"}`}
          >
            {t("runs:resume.remainingLabel")}
          </p>
          <p className={`mt-1 text-sm ${cancelled ? "text-slate-900" : "text-rose-950"}`}>
            {t("runs:resume.remainingHint", {
              count: remaining.length,
              total: STAGE_ORDER_FULL.length,
              stages: remaining.map((s) => t(`runs:stages.${s}`)).join(" → "),
            })}
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {canShowResume && (
          <Button
            type="button"
            onClick={onResume}
            disabled={resuming}
            data-testid="run-resume-button"
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {resuming ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <RotateCw className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
            )}
            {t("runs:resume.button", {
              stage: t(`runs:stages.${nextStage}`),
            })}
          </Button>
        )}
        {canWrite && (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/projects/${projectId}`)}
            data-testid="run-error-retry"
            className={
              cancelled
                ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                : "border-rose-300 bg-white text-rose-700 hover:bg-rose-50"
            }
          >
            <Sparkles className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
            {t("runs:errors.retry")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function RunDetail() {
  const { t } = useTranslation(["runs", "common", "errors", "findings", "integrations", "projects"]);
  const { id: projectId, runId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [run, setRun] = useState(null);
  const [project, setProject] = useState(null);
  const [findings, setFindings] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showStrategy, setShowStrategy] = useState(false);
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);

  const loadAll = useCallback(async (initial = false, opts = {}) => {
    const lowConf = opts.lowConf ?? includeLowConfidence;
    if (initial) setLoading(true);
    try {
      const [runRes, projRes] = await Promise.all([
        api.get(`/research/runs/${runId}`),
        api.get(`/research/projects/${projectId}`).catch(() => null),
      ]);
      setRun(runRes.data);
      if (projRes?.data) setProject(projRes.data);
      if (runRes.data.status === "completed" || runRes.data.status === "failed" || runRes.data.status === "cancelled") {
        const findingsUrl = `/research/runs/${runId}/findings${lowConf ? "?include_low_confidence=true" : ""}`;
        const [fRes, eRes, sRes] = await Promise.all([
          api.get(findingsUrl),
          api.get(`/research/runs/${runId}/evidence?page=1&page_size=100`),
          api.get(`/research/runs/${runId}/sources`),
        ]);
        setFindings(fRes.data);
        setEvidence(eRes.data?.items || []);
        setSources(sRes.data);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("errors:notFound"));
        navigate(`/projects/${projectId}`);
        return;
      }
      if (initial) toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      if (initial) setLoading(false);
    }
  }, [runId, projectId, navigate, t, includeLowConfidence]);

  const toggleLowConfidence = useCallback((next) => {
    setIncludeLowConfidence(next);
    loadAll(false, { lowConf: next });
  }, [loadAll]);

  useEffect(() => { loadAll(true); }, [loadAll]);

  // Poll while queued or running.
  useEffect(() => {
    if (!run) return;
    if (run.status === "queued" || run.status === "running") {
      const id = setInterval(() => loadAll(false), POLL_INTERVAL_MS);
      return () => clearInterval(id);
    }
  }, [run, loadAll]);

  const cancelRun = async () => {
    setCancelling(true);
    try {
      await api.post(`/research/runs/${runId}/cancel`);
      await loadAll(false);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setCancelling(false);
    }
  };

  const sourcesById = useMemo(() => Object.fromEntries(sources.map((s) => [s.id, s])), [sources]);

  if (loading || !run) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  const errorKey = run.error?.detail || "generic";

  return (
    <div className="space-y-8" data-testid="run-detail-page">
      <Link
        to={`/projects/${projectId}`}
        data-testid="run-detail-back"
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("runs:header.backToProject")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={run.status} />
            {project?.type && (
              <Badge
                variant="outline"
                data-testid="run-strategy-badge"
                title={t(`projects:frameworks.${project.type}.summary`, { defaultValue: "" })}
                className="border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-indigo-800"
              >
                {t("runs:header.strategyBadge", {
                  strategy: t(`projects:types.${project.type}`),
                  framework: t(`projects:frameworks.${project.type}.label`),
                  defaultValue: `${project.type} · ${t(`projects:frameworks.${project.type}.label`)}`,
                })}
              </Badge>
            )}
            {run.crawler_used && (
              <Badge
                variant="outline"
                data-testid="run-crawler-used-badge"
                className="border-slate-200 bg-white text-[10px] font-semibold text-slate-600"
              >
                {t("runs:header.crawledWith", {
                  provider: t(`integrations:crawler.providers.${run.crawler_used}.label`),
                })}
              </Badge>
            )}
            {/* Phase 12 — source freshness window snapshotted at run start. */}
            {run.freshness_days !== undefined && (
              <Badge
                variant="outline"
                data-testid="run-freshness-badge"
                className="border-emerald-200 bg-emerald-50 text-[10px] font-semibold text-emerald-800"
                title={t("runs:header.freshnessTitle")}
              >
                {run.freshness_days === null
                  ? t("runs:header.freshnessAll")
                  : t("runs:header.freshnessDays", { days: run.freshness_days })}
              </Badge>
            )}
            {run.stage_stats?.stale_skipped > 0 && (
              <Badge
                variant="outline"
                data-testid="run-stale-skipped-badge"
                className="border-amber-200 bg-amber-50 text-[10px] font-semibold text-amber-800"
                title={t("runs:header.staleSkippedTitle")}
              >
                {t("runs:header.staleSkipped", { count: run.stage_stats.stale_skipped })}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {run.started_at
              ? t("runs:header.runOn", { date: new Date(run.started_at).toLocaleString() })
              : t("runs:header.neverStarted")}
          </p>
        </div>
        {canWrite && (run.status === "queued" || run.status === "running") && (
          <Button
            variant="outline"
            onClick={cancelRun}
            disabled={cancelling}
            data-testid="run-cancel"
            className="border-rose-200 text-rose-700 hover:bg-rose-50"
          >
            {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                <X className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("runs:header.cancel")}
              </>
            )}
          </Button>
        )}
      </header>

      <StageStrip run={run} />

      {/* Warnings */}
      <WarningsPanel run={run} />

      {/* Search strategy (queries) */}
      <SearchStrategyPanel run={run} expanded={showStrategy} onToggle={() => setShowStrategy((s) => !s)} />

      {(run.status === "failed" || run.status === "cancelled") && (
        <ResumePanel
          run={run}
          projectId={projectId}
          canWrite={canWrite}
          onResumed={() => loadAll(false)}
        />
      )}

      {(run.status === "completed" || run.status === "failed" || run.status === "cancelled") && (
        <Tabs defaultValue="findings" className="space-y-6">
          <TabsList data-testid="run-tabs">
            <TabsTrigger value="findings" data-testid="run-tab-findings">
              {t("runs:tabs.findings")} <span className="ml-1 text-[10px] text-slate-400">{findings.length}</span>
            </TabsTrigger>
            <TabsTrigger value="evidence" data-testid="run-tab-evidence">
              {t("runs:tabs.evidence")} <span className="ml-1 text-[10px] text-slate-400">{evidence.length}</span>
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="run-tab-sources">
              {t("runs:tabs.sources")} <span className="ml-1 text-[10px] text-slate-400">{sources.length}</span>
            </TabsTrigger>
          </TabsList>

          {/* Findings */}
          <TabsContent value="findings">
            <div className="mb-3 flex items-center justify-end gap-2 text-xs text-slate-500">
              <label className="inline-flex items-center gap-2" data-testid="run-low-confidence-toggle-wrap">
                <input
                  type="checkbox"
                  checked={includeLowConfidence}
                  onChange={(e) => toggleLowConfidence(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600"
                  data-testid="run-toggle-low-confidence"
                />
                {t("findings:confidence.showLowToggle")}
              </label>
            </div>
            {findings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center" data-testid="run-no-findings">
                <p className="font-display text-sm font-semibold text-slate-900">{t("runs:empty.noFindings")}</p>
                {run.status === "completed" && evidence.length === 0 && (
                  <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{t("runs:empty.noEvidenceBody")}</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {findings.map((f) => (
                  <FindingCard key={f.id} finding={f} evidence={evidence} sourcesById={sourcesById} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Evidence */}
          <TabsContent value="evidence">
            {evidence.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center" data-testid="run-no-evidence">
                <p className="font-display text-sm font-semibold text-slate-900">{t("runs:empty.noEvidence")}</p>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{t("runs:empty.noEvidenceBody")}</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:evidence.columns.quote")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:evidence.columns.topic")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:evidence.columns.sentiment")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:evidence.columns.source")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evidence.map((e) => (
                      <tr key={e.id} data-testid={`evidence-row-${e.id}`} className="border-t border-slate-100">
                        <td className="px-6 py-4 text-slate-700">&quot;{e.quote.slice(0, 200)}{e.quote.length > 200 ? "…" : ""}&quot;</td>
                        <td className="px-6 py-4 text-slate-600">{e.topic}</td>
                        <td className="px-6 py-4">
                          <Badge variant="outline" className={SENTIMENT_BADGE[e.sentiment] || SENTIMENT_BADGE.neutral}>
                            {e.sentiment}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          {e.source_url ? (
                            <a href={e.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline">
                              {(e.source_title || e.source_url).slice(0, 50)}
                              <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                            </a>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Sources */}
          <TabsContent value="sources">
            {sources.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center" data-testid="run-no-sources">
                <p className="font-display text-sm font-semibold text-slate-900">{t("runs:empty.noSources")}</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:sources.columns.title")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:sources.columns.type")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:sources.columns.provider")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:sources.columns.relevance")}</th>
                      <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("runs:sources.columns.reliability")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => (
                      <tr key={s.id} data-testid={`source-row-${s.id}`} className="border-t border-slate-100">
                        <td className="px-6 py-4">
                          <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline">
                            {s.title || s.url}
                            <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                          </a>
                        </td>
                        <td className="px-6 py-4 text-slate-600">{s.type}</td>
                        <td className="px-6 py-4 text-slate-600">{s.provider}</td>
                        <td className="px-6 py-4">
                          {typeof s.pre_crawl_relevance === "number" ? (
                            <Badge
                              variant="outline"
                              className={
                                s.pre_crawl_relevance >= 0.6
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-amber-200 bg-amber-50 text-amber-800"
                              }
                              title={s.pre_crawl_relevance_reason || ""}
                              data-testid={`source-relevance-${s.id}`}
                            >
                              {Math.round(s.pre_crawl_relevance * 100)}%
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{Math.round((s.reliability || 0) * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
